import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { CalendarEvent, CalendarEventContextItem, CalendarEventStatus } from '../../models/calendar.types';
import { CalendarExportService } from '../../services/calendar-export.service';
import { formatGroupDateHeader, formatTimeRange } from '../../utils/calendar-date.utils';

export interface CalendarEventDialogData {
  event: CalendarEvent;
}

@Component({
  selector: 'aura-calendar-event-dialog',
  imports: [MatDialogModule, MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './calendar-event-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarEventDialogComponent {
  readonly dialogRef = inject(MatDialogRef<CalendarEventDialogComponent>);
  readonly data = inject<CalendarEventDialogData>(MAT_DIALOG_DATA);
  private readonly exportService = inject(CalendarExportService);
  private readonly router = inject(Router, { optional: true });

  get event(): CalendarEvent {
    return this.data.event;
  }

  onNavigateContext(item: CalendarEventContextItem): void {
    this.dialogRef.close();
    if (item.route && this.router) {
      const commands = Array.isArray(item.route) ? item.route : [item.route];
      void this.router.navigate(commands);
    } else if (item.url) {
      if (item.url.startsWith('http')) {
        window.open(item.url, '_blank', 'noopener,noreferrer');
      } else if (this.router) {
        void this.router.navigateByUrl(item.url);
      }
    }
  }

  get formattedDate(): string {
    return formatGroupDateHeader(new Date(this.event.startAt));
  }

  get formattedTime(): string {
    if (this.event.allDay) return 'Toute la journée';
    return formatTimeRange(this.event.startAt, this.event.endAt);
  }

  getStatusBadge(status?: CalendarEventStatus): { label: string; class: string } {
    switch (status) {
      case 'confirmed':
        return {
          label: 'Confirmé',
          class: 'bg-jade-3 text-jade-11 border-jade-6',
        };
      case 'tentative':
        return {
          label: 'En attente',
          class: 'bg-amber-3 text-amber-11 border-amber-6',
        };
      case 'completed':
        return {
          label: 'Effectué',
          class: 'bg-primary-3 text-primary-11 border-primary-6',
        };
      case 'cancelled':
        return {
          label: 'Annulé',
          class: 'bg-ruby-3 text-ruby-11 border-ruby-6',
        };
      default:
        return {
          label: 'Prévu',
          class: 'bg-neutral-3 text-neutral-11 border-neutral-6',
        };
    }
  }

  getMapsUrl(address: string): string {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  }
}
